import { useEffect, useState, useRef, useCallback } from 'react';
import { MdRefresh } from "react-icons/md";
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { useAppSelector } from '../../store/hooks.js';
import { selectTradingProviders } from '../../store/settingsSlice';
import { ProviderFactory } from '../../services/providers';
import type { TradingProviderType } from '../../services/providers';
import type { Position } from '@ocktra/shared-types';

type SortBy = 'symbol' | 'side' | 'quantity' | 'pnl' | 'pnlPercent';
type SortOrder = 'asc' | 'desc';

interface PositionWithProvider extends Position {
    providerName: string;
    connectionId: string;
}

const Positions = () => {
    const [positions, setPositions] = useState<PositionWithProvider[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
    const [sortBy, setSortBy] = useState<SortBy>('symbol');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const tradingProviders = useAppSelector(selectTradingProviders);

    const fetchPositions = useCallback(async () => {
        try {
            setIsLoading(true);
            // Get all connected trading providers
            const connectedProviders = tradingProviders.filter(p => p.status === 'connected');

            if (connectedProviders.length === 0) {
                setError('No trading provider connected');
                setIsLoading(false);
                setPositions([]);
                return;
            }

            // Fetch positions from all connected providers
            const allPositionsPromises = connectedProviders.map(async (provider) => {
                try {
                    const providerInstance = ProviderFactory.getTradingProvider(
                        provider.providerName as TradingProviderType
                    );

                    if (!providerInstance) {
                        console.warn(`Failed to initialize {provider.providerName} provider`);
                        return [];
                    }

                    providerInstance.updateConnectionDetails(provider);
                    const data = await providerInstance.getPositions();

                    // Add provider info to each position
                    return data.map(pos => ({
                        ...pos,
                        providerName: provider.providerName,
                        connectionId: provider.id
                    }));
                } catch (err) {
                    console.error(`Error fetching positions from {provider.providerName}:`, err);
                    return [];
                }
            });

            const positionsArrays = await Promise.all(allPositionsPromises);
            const allPositions = positionsArrays.flat();

            setPositions(allPositions);
            setError(null);
        } catch (err) {
            console.error('[Positions] Error fetching positions:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch positions');
        } finally {
            setIsLoading(false);
        }
    }, [tradingProviders]);

    useEffect(() => {
        fetchPositions();
    }, [fetchPositions]);

    const handleClosePosition = async (position: PositionWithProvider) => {
        try {
            const provider = tradingProviders.find(p => p.id === position.connectionId);

            if (!provider) {
                setError('Provider connection not found');
                return;
            }

            const providerInstance = ProviderFactory.getTradingProvider(
                provider.providerName as TradingProviderType
            );

            if (!providerInstance) {
                setError('Trading provider not available');
                return;
            }

            providerInstance.updateConnectionDetails(provider);
            // TODO: Implement closePosition in ITradingProvider interface
            // await providerInstance.closePosition(position.symbol);
            console.log('Close position for:', position.tradingSymbol);
            await fetchPositions(); // Refresh after closing
        } catch (err) {
            console.error('[Positions] Error closing position:', err);
            setError(err instanceof Error ? err.message : 'Failed to close position');
        }
    };

    const toggleDropdown = (index: number) => {
        setOpenDropdownIndex(prev => (prev === index ? null : index));
    };

    const handleHeaderClick = (column: SortBy) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('desc'); // Default to desc for PnL, asc for others handled below
        }
    };

    const getSortIcon = (column: SortBy) => {
        if (sortBy !== column) return null;
        return (
            <span style={{ marginLeft: 'var(--spacing-xs)' }}>
                {sortOrder === 'asc' ? '▲' : '▼'}
            </span>
        );
    };

    const getProviderInfo = (providerName: string) => {
        switch (providerName.toLowerCase()) {
            case 'kite':
                return { name: 'Kite', color: 'var(--color-primary-600)', bgColor: 'var(--color-primary-50)' };
            case 'papertrade':
                return { name: 'Paper', color: 'var(--color-warning-600)', bgColor: 'var(--color-warning-50)' };
            default:
                return { name: providerName, color: 'var(--color-gray-600)', bgColor: 'var(--color-gray-50)' };
        }
    };

    const getDropDownOptions = (position: PositionWithProvider) => {
        const isClosed = position.quantity === 0;
        const positionType = isClosed ? "CLOSED" : position.quantity > 0 ? "LONG" : "SHORT";

        switch (positionType) {
            case "LONG":
            case "SHORT":
                return (
                    <>
                        <div
                            className='px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors'
                            onClick={() => console.log("Place SL for", position.tradingSymbol)}
                        >
                            Place SL
                        </div>
                        <div
                            className='px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors'
                            onClick={() => handleClosePosition(position)}
                        >
                            Exit
                        </div>
                    </>
                );
            default:
                return (
                    <div
                        className='px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors'
                        onClick={() => console.log("Info for", position.tradingSymbol)}
                    >
                        Info
                    </div>
                );
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.dropdown-item')) {
                setOpenDropdownIndex(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center text-gray-500">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-full flex items-center justify-center text-red-600 p-4 text-center">
                {error}
            </div>
        );
    }

    if (positions.length === 0) {
        return (
            <div className="h-full flex items-center justify-center text-gray-500">
                No open positions
            </div>
        );
    }

    // Sort positions based on selected column
    const sortedPositions = [...positions].sort((a, b) => {
        let comparison = 0;

        switch (sortBy) {
            case 'symbol':
                comparison = a.tradingSymbol.localeCompare(b.tradingSymbol);
                break;
            // case 'side':
            //     comparison = a.quantity.localeCompare(b.positionType);
            //     break;
            case 'quantity':
                comparison = a.quantity - b.quantity;
                break;
            // case 'pnl':
            //     comparison = a. - b.unrealizedPnL;
            //     break;
            // case 'pnlPercent':
            //     comparison = a.unrealizedPnLPercent - b.unrealizedPnLPercent;
            //     break;
        }

        return sortOrder === 'asc' ? comparison : -comparison;
    });

    const totalPnl = sortedPositions.reduce((acc, p) => acc + (p.averagePrice), 0);

    return (
        <div
            className="flex flex-col h-full"
            style={{
                padding: 'var(--spacing-md)'
            }}
        >
            <div className="h-full overflow-auto">
                <table
                    className="w-full border-collapse"
                    style={{ fontSize: 'var(--font-size-sm)' }}
                >
                    <thead>
                        <tr
                            style={{
                                backgroundColor: 'var(--color-gray-50)',
                                borderBottom: '2px solid var(--color-border-light)'
                            }}
                        >
                            <th
                                className="text-left uppercase tracking-wider cursor-pointer"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: sortBy === 'symbol' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)',
                                    userSelect: 'none',
                                    transition: 'color var(--transition-fast)'
                                }}
                                onClick={() => handleHeaderClick('symbol')}
                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary-600)'}
                                onMouseLeave={(e) => e.currentTarget.style.color = sortBy === 'symbol' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)'}
                            >
                                Symbol{getSortIcon('symbol')}
                            </th>
                            <th
                                className="text-center uppercase tracking-wider"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--color-text-secondary)'
                                }}
                            >
                            </th>
                            <th
                                className="text-right uppercase tracking-wider cursor-pointer"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: sortBy === 'quantity' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)',
                                    userSelect: 'none',
                                    transition: 'color var(--transition-fast)'
                                }}
                                onClick={() => handleHeaderClick('quantity')}
                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary-600)'}
                                onMouseLeave={(e) => e.currentTarget.style.color = sortBy === 'quantity' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)'}
                            >
                                Qty{getSortIcon('quantity')}
                            </th>
                            <th
                                className="text-right uppercase tracking-wider"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--color-text-secondary)'
                                }}
                            >
                                Avg Price
                            </th>
                            <th
                                className="text-right uppercase tracking-wider"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--color-text-secondary)'
                                }}
                            >
                                Current
                            </th>
                            <th
                                className="text-right uppercase tracking-wider cursor-pointer"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: sortBy === 'pnl' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)',
                                    userSelect: 'none',
                                    transition: 'color var(--transition-fast)'
                                }}
                                onClick={() => handleHeaderClick('pnl')}
                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary-600)'}
                                onMouseLeave={(e) => e.currentTarget.style.color = sortBy === 'pnl' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)'}
                            >
                                P&L{getSortIcon('pnl')}
                            </th>
                            <th
                                className="text-center uppercase tracking-wider"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--color-text-secondary)'
                                }}
                            >
                                <MdRefresh
                                    size={16}
                                    className="cursor-pointer"
                                    style={{
                                        color: 'var(--color-text-tertiary)',
                                        transition: 'color var(--transition-fast), transform var(--transition-base)',
                                        display: 'inline-block'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = 'var(--color-primary-600)';
                                        e.currentTarget.style.transform = 'rotate(180deg)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = 'var(--color-text-tertiary)';
                                        e.currentTarget.style.transform = 'rotate(0deg)';
                                    }}
                                    onClick={fetchPositions}
                                    title="Refresh positions"
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        style={{
                            backgroundColor: 'var(--color-surface)',
                            borderTop: '1px solid var(--color-border-light)'
                        }}
                    >
                        {sortedPositions.map((position, index) => {
                            const isClosed = position.quantity === 0;
                            const pnlColor = position.unrealised || 1 >= 0 ? 'var(--color-success-600)' : 'var(--color-danger-600)';

                            return (
                                <tr
                                    key={`{position.tradingSymbol}-{position.connectionId}`}
                                    style={{
                                        borderBottom: '1px solid var(--color-border-light)',
                                        transition: 'background-color var(--transition-fast)',
                                        opacity: isClosed ? 0.5 : 1
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-surface)'}
                                >
                                    <td
                                        className="whitespace-nowrap"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                                            <span
                                                className="inline-flex items-center justify-center uppercase"
                                                style={{
                                                    padding: 'var(--spacing-xs) var(--spacing-sm)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    fontSize: 'var(--font-size-xs)',
                                                    fontWeight: 'var(--font-weight-bold)',
                                                    backgroundColor: getProviderInfo(position.providerName).bgColor,
                                                    color: getProviderInfo(position.providerName).color,
                                                    border: `1px solid ${getProviderInfo(position.providerName).color}33`,
                                                    minWidth: '3rem'
                                                }}
                                            >
                                                {getProviderInfo(position.providerName).name}
                                            </span>
                                            <div
                                                style={{
                                                    fontSize: 'var(--font-size-sm)',
                                                    fontWeight: 'var(--font-weight-bold)',
                                                    color: 'var(--color-text-primary)'
                                                }}
                                            >
                                                {position.tradingSymbol}
                                                <sub
                                                    style={{
                                                        fontSize: 'var(--font-size-xs)',
                                                        fontWeight: 'var(--font-weight-normal)',
                                                        color: 'var(--color-text-tertiary)',
                                                        marginLeft: 'var(--spacing-xs)'
                                                    }}
                                                >
                                                    {position.product}
                                                </sub>
                                            </div>
                                        </div>
                                    </td>
                                    <td
                                        className="whitespace-nowrap text-center"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <div className="relative" ref={dropdownRef}>
                                            <HiOutlineDotsVertical
                                                onClick={() => toggleDropdown(index)}
                                                className="cursor-pointer"
                                                style={{
                                                    color: 'var(--color-text-tertiary)',
                                                    width: '1.25rem',
                                                    height: '1.25rem',
                                                    transition: 'color var(--transition-fast)'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-text-secondary)'}
                                                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-tertiary)'}
                                            />
                                            {openDropdownIndex === index && (
                                                <div
                                                    className='absolute top-full right-0 flex flex-col overflow-hidden'
                                                    style={{
                                                        marginTop: 'var(--spacing-xs)',
                                                        backgroundColor: 'var(--color-surface)',
                                                        borderRadius: 'var(--radius-lg)',
                                                        boxShadow: 'var(--shadow-lg)',
                                                        border: '1px solid var(--color-border-light)',
                                                        zIndex: 'var(--z-index-dropdown)',
                                                        minWidth: '10rem'
                                                    }}
                                                >
                                                    {getDropDownOptions(position)}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td
                                        className="whitespace-nowrap text-right"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: 'var(--font-weight-medium)',
                                                color: 'var(--color-text-primary)'
                                            }}
                                        >
                                            {position.quantity}
                                        </span>
                                    </td>
                                    <td
                                        className="whitespace-nowrap text-right"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: 'var(--font-weight-medium)',
                                                color: 'var(--color-text-primary)'
                                            }}
                                        >
                                            {position.averagePrice.toFixed(2)}
                                        </span>
                                    </td>
                                    <td
                                        className="whitespace-nowrap text-right"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: 'var(--font-weight-medium)',
                                                color: 'var(--color-text-primary)'
                                            }}
                                        >
                                            {position.averagePrice.toFixed(2)}
                                        </span>
                                    </td>
                                    <td
                                        className="whitespace-nowrap text-right"
                                        style={{ padding: 'var(--spacing-xs)' }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: 'var(--font-weight-semibold)',
                                                color: pnlColor
                                            }}
                                        >
                                            {position.unrealised || 1 >= 0 ? '+' : ''}{(position.unrealised || 1).toFixed(2)}
                                        </span>
                                    </td>
                                    <td style={{ padding: 'var(--spacing-xs)' }}></td>
                                </tr>
                            );
                        })}
                        <tr
                            style={{
                                borderTop: '2px solid var(--color-border-light)',
                                backgroundColor: 'var(--color-gray-50)'
                            }}
                        >
                            <td
                                colSpan={5}
                                className="text-right"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 'var(--font-weight-bold)',
                                    color: 'var(--color-text-primary)'
                                }}
                            >
                                Total P&L
                            </td>
                            <td
                                className="text-right"
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 'var(--font-weight-bold)',
                                    color: totalPnl >= 0 ? 'var(--color-success-600)' : 'var(--color-danger-600)'
                                }}
                            >
                                {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(2)}
                            </td>
                            <td colSpan={3}></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Positions;
